import { NgModule, inject } from '@angular/core';
import { RouterModule, ROUTES } from '@angular/router';
import { ErrorComponent } from './error/default/error.component';
import { PageNotFoundComponent } from './error/page-not-found/page-not-found.component';
import { BadConnectionGuard } from './cockpit/bad-connection/bad-connection.guard';
import { BadConnectionComponent } from './cockpit/bad-connection/bad-connection.component';
import { UnlockComponent } from './unlock/unlock.component';
import { VerifyEmailComponent } from './utility/verify-email/verify-email.component';
import { SetEmailComponent } from './utility/set-email/set-email.component';
import { RequestPasswordComponent } from './utility/request-password/request-password.component';
import { SetPasswordComponent } from './utility/set-password/set-password.component';
import { TermsOfUseAndPrivacyPolicyAgreementComponent } from './utility/terms-of-use-and-privacy-policy-agreement/terms-of-use-and-privacy-policy-agreement.component';
import { ServiceNotAvailableComponent } from './error/service-not-available/service-not-available.component';
import { StartService } from './cockpit/start/start.service';
import { LoginGuard } from './guards/login.guard';
import { NoUnlockGuard } from './guards/no-unlock.guard';
import { AppGuard } from './guards/app.guard';
import { LogoutComponent } from './logout/logout.component';
import { UpdateRequiredComponent } from './error/update-required/update-required.component';
import { UpdateGuard } from './guards/update.guard';
import { MigrationComponent } from './utility/migration/migration.component';
import { ConfigService } from './config.service';
import { LowPermissionAccessComponent } from './low-permission-access/low-permission-access.component';
import { PublicSurveyComponent } from './public-survey/public-survey.component';
import { environment } from '../environments_mc/environment';
import { SystemEnvironment } from '@mono-repo/data';
import { APP_BASE_HREF } from '@angular/common';
import { appConfiguration } from '../configuration';

@NgModule({
  imports: [
    RouterModule.forRoot([], {
      useHash:
        environment.systemEnvironment === SystemEnvironment.CLOUD_FOUNDRY && environment.scpId === 'noHash' ?
          false
        : true,
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: ROUTES,
      useFactory: () => {
        const disableCockpit: boolean = inject(ConfigService).getConfig()?.disableCockpitAccess;
        const initialRoute: string = inject(ConfigService).getConfig()?.initialRoute || appConfiguration.initialRoute;
        return [
          {
            path: 'login',
            loadChildren: disableCockpit ? undefined : () => import('./login/login.module').then((m) => m.LoginModule),
            canActivate: disableCockpit ? undefined : [UpdateGuard, LoginGuard],
            redirectTo: disableCockpit ? 'lpa' : undefined,
          },
          {
            path: 'loginAndRegistration',
            loadChildren:
              disableCockpit ? undefined : (
                () =>
                  import('./login-and-registration/login-and-registration.module').then(
                    (m) => m.LoginAndRegistrationModule,
                  )
              ),
            canActivate: disableCockpit ? undefined : [UpdateGuard, LoginGuard],
            redirectTo: disableCockpit ? 'lpa' : undefined,
          },
          {
            path: 'logout',
            component: disableCockpit ? undefined : LogoutComponent,
            redirectTo: disableCockpit ? 'lpa' : undefined,
          },
          {
            path: 'error',
            component: disableCockpit ? LowPermissionAccessComponent : ErrorComponent,
          },
          {
            path: 'serviceNotAvailable',
            component: ServiceNotAvailableComponent,
          },
          {
            path: 'badConnection',
            component: BadConnectionComponent,
          },
          {
            path: 'updateRequired',
            component: UpdateRequiredComponent,
          },
          {
            path: 'termsOfUseAndPrivacyPolicyAgreement',
            component: TermsOfUseAndPrivacyPolicyAgreementComponent,
          },
          {
            path: 'unlock',
            component: UnlockComponent,
            canActivate: [NoUnlockGuard],
          },
          {
            path: 'requestpassword',
            component: RequestPasswordComponent,
            // canActivate: [AppGuard]
          },
          {
            path: 'migration',
            component: MigrationComponent,
          },
          {
            path: 'setpassword/:token',
            component: SetPasswordComponent,
            canActivate: [AppGuard],
          },
          {
            path: 'verifyemail/:token',
            component: VerifyEmailComponent,
            canActivate: [AppGuard],
          },
          {
            path: 'newemail/:token',
            component: SetEmailComponent,
            canActivate: [AppGuard],
          },
          {
            path: 'publicSurvey/:surveyId',
            component: PublicSurveyComponent,
          },
          {
            path: 'cockpit',
            loadChildren:
              disableCockpit ? undefined : () => import('./cockpit/cockpit.module').then((m) => m.CockpitModule),
            canActivate: disableCockpit ? undefined : [BadConnectionGuard, UpdateGuard, AppGuard],
            canActivateChild: [BadConnectionGuard],
            resolve: {
              tiles: StartService,
            },
            redirectTo: disableCockpit ? 'lpa' : undefined,
          },
          {
            path: 'lpa',
            loadChildren: () =>
              import('./low-permission-access/low-permission-access.module').then((m) => m.LowPermissionAccessModule),
          },
          {
            path: 'id_token',
            pathMatch: 'full',
            redirectTo: initialRoute,
          },
          {
            path: '',
            pathMatch: 'full',
            redirectTo: initialRoute,
          },
          {
            path: '**',
            component: disableCockpit ? LowPermissionAccessComponent : PageNotFoundComponent,
          },
        ];
      },
      multi: true,
    },
  ],
})
export class AppRoutingModule {
  constructor(public configService: ConfigService) {}
}
